import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TutorialPricingSection from "@/components/TutorialPricingSection";
import { Download, Settings, Key, AlertCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import tutorialImage from "@/assets/installation-iptv-mac-apple.jpg";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

const IptvMac = () => {
  useAffiliateTracking();
  const alternativeLinks = [
    "http://az.dmsmarters2.com",
    "http://d875.dtv.homes:8080",
    "http://dm.pixehd.me:2082",
    "http://dm.opixtv.com:2082",
    "http://dreamsmart.me:2082",
    "http://chrf.25atg09.6g.in"
  ];

  return (
    <>
      <Helmet>
        <title>Tutoriel IPTV Mac - Installation Smarters Player | RoiDuStreaming</title>
        <meta name="description" content="Guide complet pour installer et configurer l'IPTV sur Mac avec Smarters Player Lite. Tutoriel étape par étape." />
        <meta name="keywords" content="IPTV Mac, IPTV MacBook, Smarters Player Lite, tutoriel IPTV macOS, installer IPTV Apple" />
        <link rel="canonical" href="https://roidustreaming.fr/tutoriels/iptv-mac" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[50vh] flex items-center justify-center px-4 py-20 hero-bg">
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background"></div>
          <div className="relative z-10 max-w-5xl mx-auto text-center">
            <span className="text-7xl mb-6 block">💻</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
              Tutoriel IPTV – Mac
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              Installation avec Smarters Player Lite
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Image Mac */}
            <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={tutorialImage} 
                alt="Mac pour tutoriel IPTV" 
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Étape 1 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Download className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">1️⃣ Télécharger l'application</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Ouvre l'<strong>App Store</strong> sur ton Mac.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Recherche « <strong>Smarters Player Lite</strong> » et installe-la.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">👉</span>
                  <span>
                    Lien direct :{" "}
                    <a 
                      href="https://apps.apple.com/fr/app/smarters-player-lite/id1628995509" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Smarters Player Lite sur l'App Store
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-2 mt-4 p-4 bg-muted/50 rounded-lg">
                  <span className="text-primary">💡</span>
                  <span className="text-sm">
                    Si vous connaissez déjà une application IPTV qui fonctionne bien sur votre appareil, vous pouvez l'utiliser.
                  </span>
                </li>
              </ul>
            </div>

            {/* Étape 2 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Settings className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">2️⃣ Ouvrir et configurer</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Lance l'application.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Accepte les conditions générales.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Clique sur « <strong>Add Your Playlist via XC API / XTREAMCODE</strong> ».</span>
                </li>
              </ul>
            </div>

            {/* Étape 3 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Key className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">3️⃣ Entrer les identifiants IPTV</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Nom du profil</strong> : choisis un nom (ex. Mon IPTV).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Nom d'utilisateur</strong> : celui fourni par RoiDuStreaming.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Mot de passe</strong> : celui fourni par RoiDuStreaming.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>URL du serveur</strong> : celle fournie par RoiDuStreaming.</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-foreground font-medium">
                  ✔️ Vérifie puis appuie sur « <strong>ADD USER</strong> ».
                </p>
              </div>
            </div>

            {/* Étape 4 - Dépannage */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">4️⃣ Conseils & Dépannage</h2>
              </div>
              
              <div className="mb-6">
                <p className="text-foreground/90 mb-4">
                  👉 <strong>Si le 1er lien ne fonctionne pas</strong>, essaye l'un de ces liens alternatifs :
                </p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  {alternativeLinks.map((link, index) => (
                    <code key={index} className="block text-sm text-primary font-mono">
                      {link}
                    </code>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-foreground/90">
                  👉 <strong>Si les films/séries ne chargent pas</strong> :
                </p>
                <ul className="mt-2 space-y-2 text-foreground/80">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Relance l'app.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Si ça persiste → teste un autre lien.</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </section>

        {/* Pricing Section */}
        <TutorialPricingSection />
        
      </div>
    </>
  );
};

export default IptvMac;
